/// Port of mobile/lib/game/ground.dart.

import { PositionComponent } from './engine/component';
import type { Sprite } from './engine/sprite';
import { SpriteComponent, sprite } from './engine/sprite';
import { anchors } from './engine/vector';
import { groundLevelY, worldWidth } from './world';

export const groundHeight = 12.0;
/// Matches the sprite width; do not change without re-cutting the art.
export const groundTileWidth = 1200.0;

export class Ground extends PositionComponent {
  private tiles: SpriteComponent[] = [];
  private groundSprite: Sprite;
  private isDarkMode: boolean;

  private readonly isPlaying: () => boolean;
  private readonly currentSpeed: () => number;

  constructor(isDarkMode: boolean, isPlaying: () => boolean, currentSpeed: () => number) {
    super({ position: { x: 0, y: groundLevelY }, priority: 0 });

    this.isDarkMode = isDarkMode;
    this.isPlaying = isPlaying;
    this.currentSpeed = currentSpeed;

    this.groundSprite = this.loadSprite();
    this.initializeTiles();
  }

  reloadThemeAssets(isDarkMode: boolean): void {
    this.isDarkMode = isDarkMode;
    this.groundSprite = this.loadSprite();
    for (const tile of this.tiles) {
      tile.sprite = this.groundSprite;
    }
  }

  private loadSprite(): Sprite {
    const mode = this.isDarkMode ? 'dark' : 'light';
    return sprite(`new/ground/${mode}.png`, { x: groundTileWidth, y: groundHeight });
  }

  private initializeTiles(): void {
    // One 1200px tile always covers the 600px screen; the rest wait in queue.
    const tilesNeeded = 2 + Math.ceil(worldWidth / groundTileWidth);

    for (let i = 0; i < tilesNeeded; i += 1) {
      const tile = new SpriteComponent({
        sprite: this.groundSprite,
        size: { x: groundTileWidth, y: groundHeight },
        anchor: anchors.bottomLeft,
        position: { x: groundTileWidth * i, y: 0 },
      });
      this.tiles.push(tile);
      this.add(tile);
    }
  }

  /// Fixed-timestep ground scroll + tile recycle.
  stepPhysics(dt: number): void {
    if (!this.isPlaying() || this.tiles.length === 0) return;

    const increment = this.currentSpeed() * dt;
    for (const tile of this.tiles) {
      tile.position.x -= increment;
    }

    while (this.tiles[0].position.x <= -groundTileWidth) {
      const first = this.tiles.shift()!;
      const last = this.tiles[this.tiles.length - 1];
      first.position.x = last == null ? 0 : last.position.x + groundTileWidth;
      this.tiles.push(first);
    }
  }

  reset(): void {
    this.tiles.forEach((tile, index) => {
      tile.position.x = index * groundTileWidth;
    });
  }
}
